/**
 * View model for the analytic coverage modal, built from the C1 snapshot
 * (question/AR model). AR weights are intentionally dropped — they are
 * internal to the engine and must never be rendered (owner decision).
 */

import {resolveRequirementCoverageStatus} from '../analyticCoverageStatus';
import {PIR_STATUS} from '../../pirPayload';

function asPct(value) {
    if (value == null || value === '') {
        return null;
    }
    const n = Number(value);
    if (!Number.isFinite(n)) {
        return null;
    }
    return Math.max(0, Math.min(100, Math.round(n)));
}

function normalizeExcerpt(raw) {
    if (!raw || typeof raw !== 'object') {
        return null;
    }
    const text = raw.text != null ? String(raw.text) : '';
    const conclusion = raw.conclusion != null ? String(raw.conclusion) : '';
    if (!text && !conclusion) {
        return null;
    }
    return {
        reportId: raw.reportId != null ? String(raw.reportId) : null,
        reportTitle: raw.reportTitle || (raw.reportId != null ? String(raw.reportId) : null),
        conclusion,
        text,
    };
}

function normalizeExcerpts(list) {
    if (!Array.isArray(list)) {
        return [];
    }
    return list.map(normalizeExcerpt).filter(Boolean);
}

function normalizeAr(raw) {
    if (!raw || typeof raw !== 'object') {
        return null;
    }
    return {
        id: raw.id != null ? String(raw.id) : null,
        text: raw.text || '',
        coveragePct: asPct(raw.coveragePct),
        status: raw.status || 'not_covered',
        explanation: raw.explanation || '',
        conflictIds: Array.isArray(raw.conflictIds) ? raw.conflictIds.map(String) : [],
        excerpts: normalizeExcerpts(raw.excerpts),
    };
}

function normalizeQuestion(raw, icrNumber = null) {
    if (!raw || typeof raw !== 'object') {
        return null;
    }
    const ars = Array.isArray(raw.ars) ? raw.ars.map(normalizeAr).filter(Boolean) : [];
    const multiAr = ars.length > 1;
    const source = raw.source || null;
    return {
        id: raw.id != null ? String(raw.id) : null,
        source,
        eeiId: raw.eeiId != null ? String(raw.eeiId) : null,
        eeiNumber: raw.eeiNumber || null,
        icrNumber: source === 'ICR' ? (raw.icrNumber || icrNumber || null) : null,
        text: raw.text || '',
        coveragePct: asPct(raw.coveragePct),
        status: raw.status || 'not_covered',
        explanation: raw.explanation || '',
        hasConflict: raw.hasConflict === true
            || (Array.isArray(raw.conflictIds) && raw.conflictIds.length > 0),
        conflictIds: Array.isArray(raw.conflictIds) ? raw.conflictIds.map(String) : [],
        multiAr,
        ars,
        // Question-level excerpts are only meaningful for single-AR questions (C1)
        excerpts: multiAr ? [] : normalizeExcerpts(raw.excerpts || ars[0]?.excerpts),
    };
}

function normalizeConflict(raw) {
    if (!raw || typeof raw !== 'object') {
        return null;
    }
    return {
        id: raw.id != null ? String(raw.id) : null,
        arId: raw.arId != null ? String(raw.arId) : null,
        questionId: raw.questionId != null ? String(raw.questionId) : null,
        title: raw.title || '',
        summary: raw.summary || '',
        excerpts: normalizeExcerpts(raw.excerpts),
    };
}

/**
 * Count fully / partially answered questions using the same AR bands as the
 * Coverage tab and question bars (<10% not covered, 10–90% partial, >90% full).
 * Backend snapshot counts only treat a question as partial when ≥1 AR is ≥90%,
 * which under-counts when ARs sit in the 10–89% band (e.g. 83% question bars).
 */
export function countAnalyticQuestionStatuses(questions = []) {
    let answered = 0;
    let partial = 0;
    for (const question of questions) {
        const status = resolveRequirementCoverageStatus(question.ars, question.coveragePct);
        if (status === PIR_STATUS.COVERED) {
            answered += 1;
        } else if (status === PIR_STATUS.PARTIAL) {
            partial += 1;
        }
    }
    return {
        answered,
        partial,
        total: questions.length,
    };
}

/**
 * Prefer additive analyticCoverage nest; keep root fields as fallback.
 * @param {object|null} snapshot Full GET /coverage payload (C1 shape).
 * @returns {object|null}
 */
function resolveAnalyticSource(snapshot) {
    if (!snapshot || typeof snapshot !== 'object') {
        return null;
    }
    const nest = snapshot.analyticCoverage;
    if (!nest || typeof nest !== 'object') {
        return snapshot;
    }
    return {
        ...snapshot,
        ...nest,
        questions: Array.isArray(nest.questions) ? nest.questions : snapshot.questions,
        conflicts: Array.isArray(nest.conflicts) ? nest.conflicts : snapshot.conflicts,
        snapshotId: nest.snapshotId ?? snapshot.snapshotId,
        computing: nest.computing ?? snapshot.computing,
    };
}

/**
 * @param {object|null} snapshot Full GET /coverage payload (C1 shape).
 * @returns {object|null} null when no analytic snapshot exists yet.
 */
export function buildAnalyticCoverageView(snapshot) {
    const src = resolveAnalyticSource(snapshot);
    if (!src?.snapshotId) {
        return null;
    }
    const icrNumber = src.icrNumber || null;
    const questions = Array.isArray(src.questions)
        ? src.questions.map((raw) => normalizeQuestion(raw, icrNumber)).filter(Boolean)
        : [];
    const conflicts = Array.isArray(src.conflicts)
        ? src.conflicts.map(normalizeConflict).filter(Boolean)
        : [];
    const headlinePct = asPct(
        src.headlinePct ?? src.overallPct ?? src.overall_score_pct,
    );
    const linkedOnlyPct = asPct(src.linkedOnlyPct ?? src.linked_only_pct);
    const discoveredPct = asPct(src.discoveredPct ?? src.discovered_pct);
    const discoveredCount = (() => {
        const raw = src.discoveredFintelCount
            ?? src.discovered_fintel_count
            ?? src.discoveredCount
            ?? src.discovered_count;
        if (raw == null || raw === '') {
            return null;
        }
        const n = Number(raw);
        return Number.isFinite(n) ? n : null;
    })();
    const linkedFintelCount = (() => {
        const raw = src.linkedFintelCount ?? src.linked_fintel_count;
        if (raw == null || raw === '') {
            return null;
        }
        const n = Number(raw);
        return Number.isFinite(n) ? n : null;
    })();

    const questionCounts = countAnalyticQuestionStatuses(questions);

    return {
        overallPct: headlinePct,
        headlinePct,
        linkedOnlyPct,
        discoveredPct,
        discoveredCount,
        linkedFintelCount,
        summarySentence: src.summarySentence || '',
        answeredCount: questionCounts.answered,
        partialCount: questionCounts.partial,
        // Only count loaded question rows. Snapshot questionsTotalCount can be
        // 8 while questions[] is empty, which used to render a fake "0/8".
        // Prefer nest-resolved src for totals/status.
        totalCount: questions.length > 0
            ? (questionCounts.total || Number(src.questionsTotalCount ?? questions.length))
            : 0,
        icrStatus: src.icrStatus || null,
        questions,
        conflicts,
        computing: Boolean(src.computing),
    };
}

/** "6/11 questions fully answered, 2/11 questions partially answered" */
export function analyticCountsText(view) {
    if (!view || !view.totalCount) {
        return '';
    }
    const total = view.totalCount;
    return `${view.answeredCount}/${total} questions fully answered, `
        + `${view.partialCount}/${total} questions partially answered`;
}

/** Analytic snapshot exists but nothing could be scored. */
export function isAnalyticNotApplicable(snapshot) {
    const src = resolveAnalyticSource(snapshot);
    return Boolean(
        src
        && src.snapshotId
        && !src.computing
        && src.overallPct == null
        && src.headlinePct == null,
    );
}
